import styled from "styled-components";
import { Popover, PopoverSurface, Button } from "@fluentui/react-components";
import {
  Dismiss16Regular,
  Dismiss20Regular,
  Checkmark20Regular,
} from "@fluentui/react-icons";
import React, { useContext, useMemo } from "react";
import { BooleanFilter } from "./BooleanFilter";
import { ColumnOptionsContext } from "./ColumnOptions";
import { DateFilter } from "./DateFilter";
import { NumberFilter } from "./NumberFilter";
import { TaxonomyFilter } from "./TaxonomyFilter";
import { TaxonomySelectFilter } from "./TaxonomySelectFilter";
import { TextFilter } from "./TextFilter";
import { Column } from "./types";
import { clearFilter } from "./utils";

type Props = {
  isOpen: boolean;
  close: () => void;
  target: React.RefObject<HTMLDivElement | null>;
  width: number;
  column: Column;
};

export const FilterDropdown = ({
  isOpen,
  close,
  target,
  width,
  column,
}: Props) => {
  const { setFilter, strings, id } = useContext(ColumnOptionsContext);

  const disableClearButton = useMemo(() => {
    if (!column.filter) return {};
    return {
      boolean:
        column.filter?.type === "boolean" && column.filter?.value === null,
      text: column.filter?.type === "text" && column.filter?.value === "",
      date: column.filter?.type === "date" && column.filter?.date1 === null,
      number:
        column.filter?.type === "number" && column.filter?.number1 === null,
      taxonomy:
        column.filter?.type === "taxonomy" && !column.filter?.value.length,
    };
  }, [column.filter]);

  if (!column.filter) return null;

  const isDisabled = disableClearButton[column.filter?.type];

  // Use the target element's current value for positioning
  const targetElement = target.current;

  if (!targetElement) return null;

  return (
    <Popover
      open={isOpen}
      // Fluent handles outside clicks and Escape, including clicks inside nested
      // pickers (calendar, listbox) that render in their own portals.
      onOpenChange={(_, data) => !data.open && close()}
      positioning={{
        target: targetElement,
        position: "below",
        align: "start",
      }}
      withArrow
    >
      <PopoverSurface
        style={{ width: width || 250 }}
        data-filter-dropdown={column.key}
        data-testid={`column-options-filter-dropdown-${column.key}`}
        aria-label={column.label}
      >
        <FilterHeader>
          <FilterTitle>{column.label}</FilterTitle>
          <Button
            appearance="subtle"
            size="small"
            icon={<Dismiss16Regular />}
            aria-label={strings.closeButtonLabel}
            onClick={close}
          />
        </FilterHeader>
        <FilterContent>
          {column.filter.type === "taxonomy" && !column.filter.isSelect && (
            <TaxonomyFilter column={column} />
          )}
          {column.filter.type === "taxonomy" && column.filter.isSelect && (
            <TaxonomySelectFilter column={column} />
          )}
          {column.filter.type === "date" && <DateFilter column={column} />}
          {column.filter.type === "number" && <NumberFilter column={column} />}
          {column.filter.type === "boolean" && (
            <BooleanFilter column={column} />
          )}
          {column.filter.type === "text" && <TextFilter column={column} />}
        </FilterContent>
        <FilterActions>
          <Button
            appearance="subtle"
            icon={<Dismiss20Regular />}
            id={id ? `${id}-clear-${column.key}` : undefined}
            disabled={isDisabled}
            onClick={() => {
              if (!column.filter) return;
              setFilter(column.key, clearFilter(column.filter));
            }}
          >
            {strings.clearFilterButtonLabel}
          </Button>
          <Button
            appearance="primary"
            icon={<Checkmark20Regular />}
            id={id ? `${id}-apply-filter-${column.key}` : undefined}
            onClick={close}
            size="small"
          >
            {strings.applyButtonLabel}
          </Button>
        </FilterActions>
      </PopoverSurface>
    </Popover>
  );
};

const FilterHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 8px 8px 0 12px;
`;

const FilterTitle = styled.span`
  font-weight: 600;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const FilterContent = styled.div`
  padding: 12px;
  overflow-y: auto;
  flex: 1;
  max-height: 300px;
`;

const FilterActions = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 8px;
  padding: 8px 12px;
  border-top: 1px solid ${(props) => props.theme.palette.neutralLight};
`;
